import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Icon } from '../primitives/Icon.js';
import { formatDuration } from '../util/format.js';
/** Один столбик waveform; высота — по пику 0..1, цвет — проигранная/непроигранная часть. */
function Bars({ peaks, ratio, onSeekRatio, }) {
    const container = {
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        height: '28px',
        flex: 1,
        minWidth: 0,
        cursor: 'pointer',
    };
    const onClick = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const r = rect.width > 0 ? (e.clientX - rect.left) / rect.width : 0;
        onSeekRatio(Math.min(1, Math.max(0, r)));
    };
    const max = peaks.length > 0 ? Math.max(...peaks, 0.0001) : 1;
    return (_jsx("div", { "data-waveform": "", style: container, onClick: onClick, role: "slider", "aria-label": "\u041F\u0435\u0440\u0435\u043C\u043E\u0442\u043A\u0430", "aria-valuenow": Math.round(ratio * 100), "aria-valuemin": 0, "aria-valuemax": 100, children: peaks.map((p, i) => {
            const played = peaks.length > 0 ? i / peaks.length <= ratio : false;
            const h = Math.max(3, Math.round((p / max) * 28));
            return (_jsx("span", { "data-bar": played ? 'played' : 'pending', style: {
                    display: 'inline-block',
                    width: '3px',
                    height: `${h}px`,
                    borderRadius: '2px',
                    background: played ? 'var(--color-accent)' : 'var(--color-text-muted)',
                    opacity: played ? 1 : 0.5,
                } }, i));
        }) }));
}
function TranscriptBlock({ voice }) {
    const t = voice.transcript;
    if (!t)
        return null;
    const box = {
        marginTop: 'var(--space-2)',
        paddingTop: 'var(--space-2)',
        borderTop: '1px solid var(--color-divider)',
        // расшифровку читают как текст сообщения — на шаг меньше --font-message, но не «подпись»
        fontSize: 'var(--font-md)',
        color: 'var(--color-text)',
    };
    if (t.status === 'ready') {
        return (_jsx("div", { "data-transcript": "ready", style: box, children: t.text ?? '' }));
    }
    if (t.status === 'failed') {
        return (_jsx("div", { "data-transcript": "failed", style: { ...box, color: 'var(--color-danger)' }, children: "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u0440\u0430\u0441\u043F\u043E\u0437\u043D\u0430\u0442\u044C" }));
    }
    return (_jsx("div", { "data-transcript": t.status, style: { ...box, color: 'var(--color-text-muted)' }, children: "\u0440\u0430\u0441\u043F\u043E\u0437\u043D\u0430\u0451\u0442\u0441\u044F\u2026" }));
}
/** Плоский плейсхолдер waveform (§4.3 деградация): peaks не приехали с сервером/метром. */
const FLAT_PEAKS = Array.from({ length: 36 }, () => 0.35);
export function VoiceMessage({ voice, isPlaying, currentTimeMs, speed = 1, onPlayPause, onSeek, onSpeed, onToggleTranscript, transcriptOpen = false, }) {
    const durationMs = voice.waveform.durationMs;
    const peaks = voice.waveform.peaks.length > 0 ? voice.waveform.peaks : FLAT_PEAKS;
    const ratio = durationMs > 0 ? Math.min(1, currentTimeMs / durationMs) : 0;
    const remainingMs = Math.max(0, durationMs - currentTimeMs);
    const hasTranscript = Boolean(voice.transcript);
    const wrap = { display: 'flex', flexDirection: 'column', minWidth: '200px' };
    const row = { display: 'flex', alignItems: 'center', gap: 'var(--space-2)' };
    return (_jsxs("div", { "data-voice": "", style: wrap, children: [_jsxs("div", { style: row, children: [_jsx("button", { type: "button", "aria-label": isPlaying ? 'Пауза' : 'Воспроизвести', onClick: onPlayPause, style: {
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '40px',
                            height: '40px',
                            borderRadius: '50%',
                            border: 'none',
                            background: 'var(--color-accent)',
                            color: 'var(--color-text-inverse)',
                            cursor: 'pointer',
                            flex: '0 0 auto',
                        }, children: _jsx(Icon, { name: isPlaying ? 'pause' : 'play', size: 20 }) }), _jsx(Bars, { peaks: peaks, ratio: ratio, onSeekRatio: (r) => onSeek(Math.round(r * durationMs)) }), _jsxs("button", { type: "button", "data-voice-speed": "", "aria-label": "\u0421\u043A\u043E\u0440\u043E\u0441\u0442\u044C \u0432\u043E\u0441\u043F\u0440\u043E\u0438\u0437\u0432\u0435\u0434\u0435\u043D\u0438\u044F", onClick: onSpeed, style: {
                            border: 'none',
                            background: 'var(--color-surface-hover)',
                            color: 'var(--color-text)',
                            borderRadius: 'var(--radius-pill)',
                            padding: '2px 8px',
                            fontSize: 'var(--font-xs)',
                            cursor: 'pointer',
                            flex: '0 0 auto',
                        }, children: [speed, "\u00D7"] })] }), _jsxs("div", { style: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginTop: '4px',
                    fontSize: 'var(--font-xs)',
                    color: 'var(--color-text-muted)',
                }, children: [_jsx("span", { "data-voice-time": "", children: formatDuration(isPlaying || currentTimeMs > 0 ? remainingMs : durationMs) }), hasTranscript ? (_jsxs("button", { type: "button", "data-transcript-toggle": "", "aria-expanded": transcriptOpen, onClick: onToggleTranscript, style: {
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '2px',
                            border: 'none',
                            background: 'transparent',
                            color: 'var(--color-accent)',
                            cursor: 'pointer',
                            fontSize: 'var(--font-xs)',
                        }, children: [transcriptOpen ? 'Скрыть текст' : 'Показать текст', _jsx(Icon, { name: "chevron-down", size: 14, style: { transform: transcriptOpen ? 'rotate(180deg)' : 'none' } })] })) : null] }), transcriptOpen ? _jsx(TranscriptBlock, { voice: voice }) : null] }));
}
//# sourceMappingURL=VoiceMessage.js.map